// ════════════════════════════════════════════════════════════════════════════
// 💳 PaymentMethodPicker — Phase 10 Payments Expansion
// ════════════════════════════════════════════════════════════════════════════
// Comprehensive payment method selector with:
//   - All payment methods: cash, card, mobile, bank_transfer, cheque,
//     gift_card, store_credit, qr_code, split, other
//   - Mobile provider selector (FriMoney, PayHere, Dialog goPay, etc.)
//   - Reference fields for cheque #, card last-4, bank ref, etc.
//   - Split payment mode
//   - Amount received / change calculation for cash
// ════════════════════════════════════════════════════════════════════════════

import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Banknote, CreditCard, Smartphone, Building2, Receipt,
  Gift, Wallet, QrCode, SplitSquareHorizontal, CircleDollarSign,
  CheckCircle2, AlertTriangle, X, ChevronDown, Loader2,
} from 'lucide-react';
import { Haptics } from '../../utils/haptics';
import CustomerPicker from './CustomerPicker';

export type PaymentMethod =
  | 'cash' | 'card' | 'mobile' | 'bank_transfer' | 'cheque'
  | 'gift_card' | 'store_credit' | 'qr_code' | 'split' | 'other' | 'credit';

export interface PaymentConfig {
  method: PaymentMethod;
  amount?: string;          // For split legs or custom amount
  reference?: string;       // Cheque #, card last4, bank ref, mobile tx ref
  provider?: string;        // Mobile / QR provider
  metadata?: Record<string, any>;
}

interface PaymentMethodPickerProps {
  grandTotal: number;
  onConfirm: (config: any) => void;
  onClose: () => void;
  isProcessing?: boolean;
  hasCustomer?: boolean;
  onCustomerAttached?: (customer: any) => void;
  storeApiKey?: string;
  attachedCustomer?: any; // 🎯 Unified Customer Integration
}

const METHOD_META: Record<PaymentMethod, { label: string; icon: any; color: string; needsRef: boolean; needsProvider: boolean }> = {
  cash:       { label: 'Cash',        icon: Banknote,        color: 'bg-emerald-500',  needsRef: false, needsProvider: false },
  card:       { label: 'Card',        icon: CreditCard,      color: 'bg-sky-500',      needsRef: true,  needsProvider: false },
  mobile:     { label: 'Mobile',      icon: Smartphone,      color: 'bg-violet-500',   needsRef: false, needsProvider: true  },
  bank_transfer: { label: 'Bank',     icon: Building2,       color: 'bg-amber-500',    needsRef: true,  needsProvider: false },
  cheque:     { label: 'Cheque',      icon: Receipt,         color: 'bg-rose-500',     needsRef: true,  needsProvider: false },
  gift_card:  { label: 'Gift Card',   icon: Gift,            color: 'bg-pink-500',     needsRef: true,  needsProvider: false },
  store_credit: { label: 'Store Credit', icon: Wallet,       color: 'bg-teal-500',     needsRef: true,  needsProvider: false },
  qr_code:    { label: 'QR Pay',      icon: QrCode,          color: 'bg-indigo-500',   needsRef: false, needsProvider: true  },
  split:      { label: 'Split',       icon: SplitSquareHorizontal, color: 'bg-orange-500', needsRef: false, needsProvider: false },
  credit:     { label: 'Pay Later',   icon: CircleDollarSign, color: 'bg-red-500',      needsRef: false, needsProvider: false },
  other:      { label: 'Other',       icon: CircleDollarSign, color: 'bg-slate-500',    needsRef: true,  needsProvider: false },
};

const MOBILE_PROVIDERS = ['FriMoney', 'PayHere', 'Dialog goPay', 'HNB SOLO', 'BOC mPay', 'ezCash', 'LankaPay', 'JustPay'];
const QR_PROVIDERS = ['LankaQR', 'FriMoney QR', 'PayHere QR', 'HNB SOLO QR', 'Visa QR', 'Mastercard QR'];

const formatRs = (n: number) => 'Rs. ' + n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PaymentMethodPicker: React.FC<PaymentMethodPickerProps> = ({ grandTotal, onConfirm, onClose, isProcessing = false, hasCustomer = false, onCustomerAttached, storeApiKey, attachedCustomer }) => {
  const [selected, setSelected] = useState<PaymentMethod>('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [reference, setReference] = useState('');
  const [provider, setProvider] = useState('');
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false); // 🎯 Search Existing Customer modal state
  const [splitMode, setSplitMode] = useState(false);
  const [splitLegs, setSplitLegs] = useState<PaymentConfig[]>([
    { method: 'cash', amount: (grandTotal / 2).toFixed(2) },
    { method: 'card', amount: (grandTotal - grandTotal / 2).toFixed(2) },
  ]);

  // Credit / Pay Later Options
  const [creditPromiseDays, setCreditPromiseDays] = useState('5');
  const [creditCustomDate, setCreditCustomDate] = useState('');
  const [creditReleaseItems, setCreditReleaseItems] = useState('true');

  // Quick Customer Register States
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickName, setQuickName] = useState('');
  const [quickPhone, setQuickPhone] = useState('');
  const [quickSaving, setQuickSaving] = useState(false);

  // Broker / Middleman Commission States
  const [hasBroker, setHasBroker] = useState(false);
  const [brokerName, setBrokerName] = useState('');
  const [brokerCommission, setBrokerCommission] = useState('');

  const handleQuickAdd = async () => {
    if (!quickName.trim() || !quickPhone.trim()) {
      alert('Name and Phone are required!');
      return;
    }
    setQuickSaving(true);
    try {
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: storeApiKey, name: quickName.trim(), phone: quickPhone.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        Haptics.fire('success');
        if (onCustomerAttached) {
          onCustomerAttached(data.customer);
        }
        setQuickAddOpen(false);
        setQuickName('');
        setQuickPhone('');
      } else {
        Haptics.fire('warn');
        alert(data.error || 'Failed to add customer');
      }
    } catch {
      alert('Failed to connect to server');
    } finally {
      setQuickSaving(false);
    }
  };

  // Reset state when grandTotal changes
  useEffect(() => {
    setCashReceived('');
    setReference('');
    setProvider('');
    setSplitMode(false);
    setCreditPromiseDays('5');
    setCreditCustomDate('');
    setCreditReleaseItems('true');
    setHasBroker(false);
    setBrokerName('');
    setBrokerCommission('');
    setSplitLegs([
      { method: 'cash', amount: (grandTotal / 2).toFixed(2) },
      { method: 'card', amount: (grandTotal - grandTotal / 2).toFixed(2) },
    ]);
  }, [grandTotal]);

  const change = useMemo(() => {
    if (selected !== 'cash') return 0;
    const received = parseFloat(cashReceived || '0');
    return received > grandTotal ? +(received - grandTotal).toFixed(2) : 0;
  }, [selected, cashReceived, grandTotal]);

  const isCashValid = selected === 'cash' && parseFloat(cashReceived || '0') >= grandTotal;

  const handleConfirm = () => {
    Haptics.fire('success');
    
    const isCreditActive = splitMode ? splitLegs.some(l => l.method === 'credit') : selected === 'credit';
    if (isCreditActive && !hasCustomer) {
      Haptics.fire('warn');
      return; // Customer is required!
    }

    let creditDetails: any = {};
    if (isCreditActive) {
      const promiseDays = parseInt(creditPromiseDays, 10) || 5;
      const promiseDate = new Date();
      promiseDate.setDate(promiseDate.getDate() + promiseDays);
      const formattedPromiseDate = creditPromiseDays === 'custom' && creditCustomDate 
        ? creditCustomDate 
        : promiseDate.toISOString().slice(0, 10);

      const status = creditReleaseItems === 'true' ? 'active' : 'items_held';
      
      let amt = 0;
      if (splitMode) {
        const leg = splitLegs.find(l => l.method === 'credit');
        amt = parseFloat(leg?.amount || '0') || 0;
      } else {
        amt = grandTotal;
      }

      creditDetails = {
        creditAmount: amt,
        creditPromiseDate: formattedPromiseDate,
        creditStatus: status
      };
    }

    const brokerDetails = hasBroker && brokerName.trim() && parseFloat(brokerCommission) > 0 ? {
      brokerName: brokerName.trim(),
      brokerCommission: parseFloat(brokerCommission) || 0
    } : {};

    if (splitMode || selected === 'split') {
      // Validate split legs
      const totalAllocated = splitLegs.reduce((s, l) => s + (parseFloat(l.amount || '0') || 0), 0);
      if (Math.abs(totalAllocated - grandTotal) > 0.01) {
        Haptics.fire('warn');
        return;
      }
      onConfirm({ splitDetails: splitLegs, ...creditDetails, ...brokerDetails });
      return;
    }

    const config: PaymentConfig = { method: selected, ...creditDetails, ...brokerDetails };
    if (selected === 'cash') {
      config.metadata = { cashReceived: parseFloat(cashReceived || '0'), change };
    }
    if (METHOD_META[selected].needsRef && reference.trim()) {
      config.reference = reference.trim();
    }
    if (METHOD_META[selected].needsProvider && provider) {
      config.provider = provider;
    }
    onConfirm(config);
  };

  const meta = METHOD_META[selected];
  const needsRef = meta.needsRef;
  const needsProvider = meta.needsProvider;
  const providers = selected === 'mobile' ? MOBILE_PROVIDERS : selected === 'qr_code' ? QR_PROVIDERS : [];

  // Split leg helpers
  const updateSplitLeg = (idx: number, patch: Partial<PaymentConfig>) => {
    setSplitLegs(prev => {
      let next = prev.map((l, i) => i === idx ? { ...l, ...patch } : l);
      if (patch.amount !== undefined) {
        if (next.length >= 2) {
          if (idx !== next.length - 1) {
            // Edited any leg except the last leg -> adjust the last leg
            const sumOfOthers = next.reduce((acc, l, i) => {
              if (i === next.length - 1) return acc;
              return acc + (parseFloat(l.amount || '0') || 0);
            }, 0);
            const bal = Math.max(0, +(grandTotal - sumOfOthers).toFixed(2));
            next[next.length - 1] = { ...next[next.length - 1], amount: bal > 0 ? bal.toString() : '' };
          } else {
            // Edited the last leg -> adjust the second-to-last leg
            const sumOfOthers = next.reduce((acc, l, i) => {
              if (i === next.length - 2) return acc;
              return acc + (parseFloat(l.amount || '0') || 0);
            }, 0);
            const bal = Math.max(0, +(grandTotal - sumOfOthers).toFixed(2));
            next[next.length - 2] = { ...next[next.length - 2], amount: bal > 0 ? bal.toString() : '' };
          }
        }
      }
      return next;
    });
  };
  const addSplitLeg = () => {
    const allocated = splitLegs.reduce((s, l) => s + (parseFloat(l.amount || '0') || 0), 0);
    const remaining = +(grandTotal - allocated).toFixed(2);
    setSplitLegs(prev => [...prev, { method: 'cash', amount: remaining > 0 ? remaining.toString() : '' }]);
  };
  const removeSplitLeg = (idx: number) => {
    if (splitLegs.length <= 2) return;
    setSplitLegs(prev => prev.filter((_, i) => i !== idx));
  };
  const splitTotal = splitLegs.reduce((s, l) => s + (parseFloat(l.amount || '0') || 0), 0);
  const splitRemaining = +(grandTotal - splitTotal).toFixed(2);
  const splitBalanced = Math.abs(splitRemaining) < 0.01;

  return (
    <div className="flex flex-col space-y-3 relative max-h-full">
      {/* Scrollable Content Container */}
      <div className="space-y-3 overflow-y-auto max-h-[calc(100vh-220px)] pr-1 pb-2">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[8px] text-slate-400 font-mono uppercase tracking-widest">Amount Due</p>
            <p className="text-[20px] font-black text-slate-800 font-mono leading-none">{formatRs(grandTotal)}</p>
          </div>
          <button onClick={onClose} className="flex items-center gap-1 text-[11px] text-slate-500 font-bold hover:text-slate-700 transition-colors">
            <X size={14} /> Back
          </button>
        </div>

      {/* Method Grid */}
      <div className="grid grid-cols-5 gap-1.5">
        {(Object.keys(METHOD_META) as PaymentMethod[]).map(m => {
          const mMeta = METHOD_META[m];
          const Icon = mMeta.icon;
          const isActive = selected === m && !splitMode;
          return (
            <button
              key={m}
              onClick={() => { Haptics.fire('tap'); setSelected(m); setSplitMode(false); }}
              className={`flex flex-col items-center gap-1 py-2 rounded-xl border text-[10px] font-bold uppercase tracking-wider transition-all ${
                isActive
                  ? 'bg-brand-surface border-brand-primary text-brand-primary-700 shadow-sm'
                  : 'border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300'
              }`}
            >
              <div className={`w-6 h-6 rounded-lg ${mMeta.color} bg-opacity-10 flex items-center justify-center`}>
                <Icon size={14} className={`${isActive ? 'text-brand-primary' : 'text-slate-400'}`} />
              </div>
              <span className="leading-none">{mMeta.label}</span>
            </button>
          );
        })}
      </div>

      {/* Split Mode Toggle (alternative to selecting Split tab) */}
      <button
        onClick={() => { Haptics.fire('tap'); setSplitMode(v => !v); }}
        className={`w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border text-[12px] font-black uppercase tracking-widest transition-all ${
          splitMode
            ? 'border-brand-primary bg-brand-surface text-brand-primary-700'
            : 'border-dashed border-slate-300 hover:border-brand-primary text-slate-500 hover:text-brand-primary'
        }`}
      >
        <SplitSquareHorizontal size={11} /> {splitMode ? 'Split Mode Active' : 'Split across methods'}
      </button>

      <AnimatePresence mode="wait">
        {splitMode ? (
          <motion.div key="split" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-2">
            {splitLegs.map((leg, idx) => {
              const legMeta = METHOD_META[leg.method];
              const LegIcon = legMeta.icon;
              return (
                <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2 border border-slate-200">
                  <select
                    value={leg.method}
                    onChange={e => updateSplitLeg(idx, { method: e.target.value as PaymentMethod })}
                    className="bg-transparent text-[11px] font-bold text-slate-700 outline-none appearance-none w-[90px]"
                  >
                    {(Object.keys(METHOD_META) as PaymentMethod[]).filter(m => m !== 'split').map(m => (
                      <option key={m} value={m}>{METHOD_META[m].label}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={leg.amount}
                    onChange={e => updateSplitLeg(idx, { amount: e.target.value })}
                    placeholder="0.00"
                    className="flex-1 bg-white border border-slate-200 rounded-lg px-2 py-1 text-[13px] font-black text-slate-800 text-right outline-none focus:border-brand-primary font-mono"
                  />
                  {splitLegs.length > 2 && (
                    <button onClick={() => removeSplitLeg(idx)} className="text-slate-400 hover:text-red-500 transition-colors">
                      <X size={14} />
                    </button>
                  )}
                </div>
              );
            })}

            <div className="flex items-center justify-between px-1">
              <button onClick={addSplitLeg} className="text-[10px] font-bold text-brand-primary hover:text-brand-primary-700 transition-colors flex items-center gap-1">
                <SplitSquareHorizontal size={11} /> Add leg
              </button>
              <div className="text-[11px] font-mono">
                <span className={splitBalanced ? 'text-emerald-600 font-bold' : 'text-amber-600 font-bold'}>
                  {splitRemaining === 0 ? 'Balanced' : splitRemaining > 0 ? `+${formatRs(splitRemaining)}` : `${formatRs(splitRemaining)}`}
                </span>
              </div>
            </div>

            {!splitBalanced && (
              <div className="flex items-center gap-1.5 text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                <AlertTriangle size={12} /> Totals must equal {formatRs(grandTotal)}
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div key="single" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-2">
            {/* Cash input */}
            {selected === 'cash' && (
              <div className="space-y-2">
                <label className="text-[9px] text-slate-400 uppercase tracking-widest font-mono">Cash Received (Rs.)</label>
                <div className="relative">
                  <input
                    type="number" min={0} placeholder="0.00" autoFocus
                    value={cashReceived}
                    onChange={e => setCashReceived(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && isCashValid) { e.preventDefault(); handleConfirm(); }}}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 pr-12 text-xl font-black text-slate-800 text-right outline-none focus:border-brand-primary font-mono transition-colors"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {isCashValid ? <CheckCircle2 size={18} className="text-emerald-500" /> : <AlertTriangle size={18} className="text-amber-400" />}
                  </div>
                </div>

                {/* ⚡ QUICK CASH PRESETS */}
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                  {(() => {
                    const presets = [grandTotal];
                    const notes = [100, 200, 500, 1000, 5000];
                    for (const note of notes) {
                      if (note > grandTotal) {
                        presets.push(note);
                      }
                    }
                    const uniquePresets = Array.from(new Set(presets)).sort((a, b) => a - b).slice(0, 4);
                    return uniquePresets.map((amt, idx) => {
                      const isExact = amt === grandTotal;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setCashReceived(amt.toString())}
                          className={`px-2.5 py-1 rounded-lg text-[9px] font-black tracking-wider transition-all border ${
                            parseFloat(cashReceived) === amt
                              ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                          }`}
                        >
                          {isExact ? 'EXACT' : `Rs. ${amt.toLocaleString()}`}
                        </button>
                      );
                    });
                  })()}
                </div>

                {change > 0 && (
                  <div className="flex items-center justify-between px-1">
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Change</span>
                    <span className="text-[13px] font-black font-mono text-emerald-600">{formatRs(change)}</span>
                  </div>
                )}
                {!isCashValid && cashReceived && (
                  <div className="text-[10px] text-red-500 font-bold flex items-center gap-1">
                    <AlertTriangle size={10} /> Insufficient cash
                  </div>
                )}
              </div>
            )}

            {/* Provider selector (mobile / QR) */}
            {needsProvider && providers.length > 0 && (
              <div className="space-y-1.5">
                <label className="text-[9px] text-slate-400 uppercase tracking-widest font-mono">Provider</label>
                <div className="relative">
                  <select
                    value={provider}
                    onChange={e => setProvider(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[12px] font-bold text-slate-800 outline-none focus:border-brand-primary appearance-none transition-colors"
                  >
                    <option value="">Select provider…</option>
                    {providers.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Reference input */}
            {needsRef && (
              <div className="space-y-1.5">
                <label className="text-[9px] text-slate-400 uppercase tracking-widest font-mono">
                  {selected === 'card' ? 'Last 4 Digits' : selected === 'cheque' ? 'Cheque Number' : selected === 'bank_transfer' ? 'Bank Reference' : selected === 'gift_card' ? 'Gift Card Code' : selected === 'store_credit' ? 'Credit Note' : 'Reference'}
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={e => setReference(e.target.value)}
                  placeholder={selected === 'card' ? '•••• 1234' : 'Enter reference…'}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[12px] font-bold text-slate-800 outline-none focus:border-brand-primary transition-colors font-mono"
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 💳 CREDIT/DEBT REMINDERS & OPTIONS (Real-world Retail Credit & Laidaway) */}
      {(() => {
        const isCreditActive = splitMode ? splitLegs.some(l => l.method === 'credit') : selected === 'credit';
        return isCreditActive && (
          <div className="p-3 bg-red-50/70 border border-red-200/50 rounded-xl space-y-2 mt-1">
            <div className="flex items-center gap-1.5 text-[10px] font-black text-red-700 uppercase tracking-wider">
              <CircleDollarSign size={13} /> Credit / Pay Later Options
            </div>

            {!hasCustomer ? (
              <div className="flex flex-col gap-2.5 p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-[10px] text-amber-800 leading-normal">
                <div className="flex items-start gap-2 font-semibold">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5 text-amber-600" />
                  <span>Customer is REQUIRED for Pay Later (Credit) sales! Please select or add a customer first.</span>
                </div>
                
                {storeApiKey && onCustomerAttached && (
                  <div className="mt-1.5 border-t border-amber-200/60 pt-2.5 space-y-2.5">
                    {!quickAddOpen ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          onClick={() => { Haptics.fire('tap'); setCustomerPickerOpen(true); }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-wider text-[10px] shadow-sm transition-all active:scale-[0.98]"
                        >
                          🔍 Search Existing Customer
                        </button>
                        <button
                          onClick={() => { Haptics.fire('tap'); setQuickAddOpen(true); }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-wider text-[10px] shadow-sm transition-all active:scale-[0.98]"
                        >
                          ➕ Register New Customer
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2.5 pt-1 text-slate-800">
                        <p className="font-bold text-[10px] uppercase text-slate-500 tracking-wider">Quick Register New Customer</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-0.5">
                            <label className="text-[8px] uppercase tracking-wider text-slate-400 font-bold font-mono">Name *</label>
                            <input
                              type="text" placeholder="Name"
                              value={quickName} onChange={e => setQuickName(e.target.value)}
                              className="w-full bg-white border border-slate-200 rounded-xl px-2.5 py-2 text-[11px] font-bold outline-none focus:border-emerald-500 transition-colors"
                            />
                          </div>
                          <div className="space-y-0.5">
                            <label className="text-[8px] uppercase tracking-wider text-slate-400 font-bold font-mono">Phone *</label>
                            <input
                              type="tel" placeholder="Phone"
                              value={quickPhone} onChange={e => setQuickPhone(e.target.value)}
                              className="w-full bg-white border border-slate-200 rounded-xl px-2.5 py-2 text-[11px] font-bold font-mono outline-none focus:border-emerald-500 transition-colors"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setQuickAddOpen(false)}
                            className="flex-1 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold uppercase text-[10px] transition-all"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleQuickAdd}
                            disabled={quickSaving || !quickName || !quickPhone}
                            className="flex-1 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] shadow-sm transition-all disabled:opacity-40 flex items-center justify-center gap-1"
                          >
                            {quickSaving ? <Loader2 size={11} className="animate-spin" /> : null}
                            {quickSaving ? 'Saving…' : 'Register & Attach'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Display attached customer details */}
                {attachedCustomer && (
                  <div className="flex items-center justify-between p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-[10px] font-bold text-emerald-800">
                    <div>
                      <p className="text-slate-500 text-[8px] uppercase font-mono">Attached Customer</p>
                      <p>{attachedCustomer.name} · {attachedCustomer.phone}</p>
                    </div>
                    {onCustomerAttached && (
                      <button
                        onClick={() => {
                          Haptics.fire('tap');
                          onCustomerAttached(null);
                        }}
                        className="p-1 text-slate-400 hover:text-red-500 rounded hover:bg-slate-100 transition-all"
                        title="Remove Customer"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                )}

                {/* Release Items Status */}
                <div className="space-y-0.5">
                  <label className="text-[8px] text-slate-500 uppercase tracking-widest font-mono">Release items now?</label>
                  <select
                    value={creditReleaseItems}
                    onChange={e => setCreditReleaseItems(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-800 outline-none focus:border-brand-primary"
                  >
                    <option value="true">Yes — Give items (Active Credit)</option>
                    <option value="false">No — Hold items (Laidaway / Item Held)</option>
                  </select>
                </div>

                {/* Promise Payment Date */}
                <div className="space-y-0.5">
                  <label className="text-[8px] text-slate-500 uppercase tracking-widest font-mono">Promise Payment Date</label>
                  <select
                    value={creditPromiseDays}
                    onChange={e => setCreditPromiseDays(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-800 outline-none focus:border-brand-primary"
                  >
                    <option value="3">In 3 Days</option>
                    <option value="5">In 5 Days</option>
                    <option value="7">In 7 Days (1 Week)</option>
                    <option value="10">In 10 Days</option>
                    <option value="14">In 14 Days (2 Weeks)</option>
                    <option value="30">In 30 Days (1 Month)</option>
                    <option value="custom">Custom Date…</option>
                  </select>

                  {creditPromiseDays === 'custom' && (
                    <input
                      type="date"
                      value={creditCustomDate}
                      onChange={e => setCreditCustomDate(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-800 outline-none focus:border-brand-primary font-mono mt-1"
                    />
                  )}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* 🤝 BROKER / MIDDLEMAN COMMISSION (Optional) */}
      <div className="p-3 bg-orange-50/70 border border-orange-200/50 rounded-xl space-y-2 mt-1">
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={hasBroker}
            onChange={e => setHasBroker(e.target.checked)}
            className="rounded text-orange-500 focus:ring-orange-500/20"
          />
          <span className="text-[10px] font-black text-orange-800 uppercase tracking-wider flex items-center gap-1">
            🤝 Introduce Middleman / Broker?
          </span>
        </label>

        {hasBroker && (
          <div className="space-y-2 pt-1">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-0.5">
                <label className="text-[8px] text-slate-500 uppercase tracking-widest font-mono font-bold">Broker Name *</label>
                <input
                  type="text" required
                  value={brokerName}
                  onChange={e => setBrokerName(e.target.value)}
                  placeholder="Broker's name…"
                  className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-800 outline-none focus:border-brand-primary"
                />
              </div>

              <div className="space-y-0.5">
                <label className="text-[8px] text-slate-500 uppercase tracking-widest font-mono font-bold">Commission (Rs.) *</label>
                <input
                  type="number" required min={0}
                  value={brokerCommission}
                  onChange={e => setBrokerCommission(e.target.value)}
                  placeholder="Commission…"
                  className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold text-slate-800 outline-none focus:border-brand-primary font-mono"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Sticky Confirm button — pinned to bottom of sidebar so it is always 100% visible */}
      <div className="sticky bottom-0 z-30 pt-2 pb-1 bg-white/95 backdrop-blur-md border-t border-slate-100 shadow-[0_-8px_16px_rgba(0,0,0,0.06)]">
        <motion.button
          whileTap={isProcessing ? {} : { scale: 0.97 }}
          onClick={handleConfirm}
          disabled={isProcessing || (splitMode ? !splitBalanced : selected === 'cash' ? !isCashValid : false) || ((splitMode ? splitLegs.some(l => l.method === 'credit') : selected === 'credit') && !hasCustomer)}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-black text-[13px] uppercase tracking-widest shadow-xl shadow-emerald-500/25 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isProcessing
            ? <><Loader2 size={16} className="animate-spin" /> Processing…</>
            : <><CheckCircle2 size={16} /> Confirm Payment</>}
        </motion.button>
      </div>

      {/* 🎯 CUSTOMER PICKER MODAL (Search & Attach Existing Customer) */}
      <CustomerPicker
        open={customerPickerOpen}
        storeApiKey={storeApiKey || ''}
        onClose={() => setCustomerPickerOpen(false)}
        onPick={(customer) => {
          Haptics.fire('success');
          if (onCustomerAttached) {
            onCustomerAttached(customer);
          }
          setCustomerPickerOpen(false);
        }}
      />
    </div>
  );
};

export default PaymentMethodPicker;
